import {
    createStyles,
    makeStyles,
    Step,
    StepLabel,
    Stepper,
} from "@material-ui/core";
import classnames from "classnames";
import { format, isBefore } from "date-fns";
import * as React from "react";

import {
    CheckIcon,
    CircleIcon,
    ClockIcon,
    currencyFormatter,
    RelodeTheme,
    Text,
    TimesIcon,
} from "../../";
import {
    AccountingStatusType,
    BillType,
    RewardBillType,
    RewardMilestone,
} from "../../domain";
import { ExpansionPanels, Panel } from "../expansionPanels";

const useStyles = makeStyles((theme: RelodeTheme) =>
    createStyles({
        panelHeader: {
            display: "grid",
            flexGrow: 1,
            gridTemplateColumns: "1fr 1fr",
            alignItems: "baseline",
            [theme.breakpoints.down("md")]: {
                gridTemplateColumns: "auto",
            },
        },
        iconContainer: {
            fontSize: "0.75rem",
            borderRadius: "50%",
            color: theme.palette.common.white,
            backgroundColor: theme.palette.action.disabledBackground,
            padding: theme.spacing(1),
            marginBottom: theme.spacing(1),

            "& svg": {
                fontSize: "inherit",
            },
        },
        iconContainerStep0Completed: {
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.common.white,
            border: `solid 1px ${theme.palette.primary.main}`,
        },
        iconContainerApproved: {
            color: theme.palette.common.white,
            backgroundColor: theme.palette.secondary.main,
        },
        iconContainerCancelled: {
            color: theme.palette.common.white,
            backgroundColor: theme.palette.error.main,
        },
        stepper: {
            padding: theme.spacing(0, 3, 2, 0),
            flexGrow: 1,
        },
        stepLabelContainer: {
            paddingLeft: theme.spacing(1),
        },
        stepHeaderRow: {
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
        },
        stepHeader: {
            color: theme.palette.common.black,
            // eslint-disable-next-line quotes
            fontFamily: ['"hkgrotesk_bold"', "Helvetica", "sans-serif"].join(
                ","
            ),
            fontSize: 18,
        },
    })
);

interface RewardsSummaryProps {
    rewardBillTypes: RewardBillType[];
    showPayeeFullName?: boolean;
}

export const RewardsSummary = (props: RewardsSummaryProps) => {
    const { showPayeeFullName = false, rewardBillTypes } = props;
    const classes = useStyles(props);

    if ((rewardBillTypes?.length ?? 0) === 0) return null;

    return (
        <ExpansionPanels id="rewardBillTypesExpansionPanels">
            {rewardBillTypes.map((rewardBillType, billTypeIndex) => {
                const currentBillType = rewardBillType.billType;

                return rewardBillType.payees.map((rewardPayee, payeeIndex) => {
                    const {
                        payeeUserId,
                        payeeFullName,
                        milestones,
                        startDate,
                    } = rewardPayee;

                    return (
                        <Panel
                            id={`${payeeUserId}-${currentBillType.id}`}
                            heading={
                                <div className={classes.panelHeader}>
                                    <Text variant="h4">
                                        {currentBillType.name}
                                    </Text>
                                    <Text variant="subtitle1">
                                        {showPayeeFullName ? payeeFullName : ""}
                                    </Text>
                                </div>
                            }
                            children={
                                <RewardMilestonesTimeline
                                    rewardMilestones={milestones}
                                    startDate={startDate}
                                    billType={currentBillType}
                                />
                            }
                        />
                    );
                });
            })}
        </ExpansionPanels>
    );
};

interface RewardMilestonesTimelineProps {
    rewardMilestones: RewardMilestone[];
    startDate: number;
    billType: BillType;
}

const RewardMilestonesTimeline = (props: RewardMilestonesTimelineProps) => {
    const { rewardMilestones, startDate, billType } = props;
    const classes = useStyles(props);

    const step0Completed = isBefore(startDate, new Date());

    return (
        <Stepper orientation="vertical" className={classes.stepper}>
            <Step completed={step0Completed}>
                <StepLabel
                    classes={{
                        labelContainer: classes.stepLabelContainer,
                        iconContainer: classnames(classes.iconContainer, {
                            [classes.iconContainerStep0Completed]: step0Completed,
                        }),
                    }}
                    icon={step0Completed ? <CircleIcon /> : <ClockIcon />}
                    optional={
                        <RewardMilestoneStartOptionalLabel
                            billType={billType}
                            startDate={startDate}
                        />
                    }
                >
                    <div className={classes.stepHeaderRow}>
                        <RewardMilestoneStartLabel billType={billType} />
                    </div>
                </StepLabel>
            </Step>
            {rewardMilestones.map((rewardMilestone, i) => {
                const {
                    amountInUSDollars,
                    accountingStatusTimestamp,
                    accountingStatusType,
                } = rewardMilestone;

                let icon = <TimesIcon />;
                switch (accountingStatusType) {
                    case AccountingStatusType.VOID:
                        icon = <TimesIcon />;
                        break;
                    case AccountingStatusType.CREDITED:
                        icon = <TimesIcon />;
                        break;
                    case AccountingStatusType.PENDING:
                        icon = <ClockIcon />;
                        break;
                    case AccountingStatusType.CONFIRMED:
                        icon = <ClockIcon />;
                        break;
                    case AccountingStatusType.APPROVED:
                        icon = <CheckIcon />;
                        break;
                }

                return (
                    <Step key={i + 1} completed={!!accountingStatusTimestamp}>
                        <StepLabel
                            classes={{
                                labelContainer: classes.stepLabelContainer,
                                iconContainer: classnames(
                                    classes.iconContainer,
                                    {
                                        [classes.iconContainerApproved]:
                                            accountingStatusType ===
                                            AccountingStatusType.APPROVED,
                                        [classes.iconContainerCancelled]:
                                            accountingStatusType ===
                                            AccountingStatusType.VOID,
                                        [classes.iconContainerCancelled]:
                                            accountingStatusType ===
                                            AccountingStatusType.CREDITED,
                                    }
                                ),
                            }}
                            icon={icon}
                            optional={
                                <RewardMilestoneOptionalLabel
                                    rewardMilestone={rewardMilestone}
                                    billType={billType}
                                />
                            }
                        >
                            <div className={classes.stepHeaderRow}>
                                <Text className={classes.stepHeader}>
                                    {currencyFormatter.format(
                                        amountInUSDollars
                                    )}
                                </Text>
                                <RewardMilestoneStatusLabel
                                    rewardMilestone={rewardMilestone}
                                />
                            </div>
                        </StepLabel>
                    </Step>
                );
            })}
        </Stepper>
    );
};

const RewardMilestoneStartLabel = (props: { billType: BillType }) => {
    const { billType } = props;
    const classes = useStyles(props);

    if (billType.isSubmission()) {
        return <Text className={classes.stepHeader}>submitted</Text>;
    } else if (billType.isHire()) {
        return <Text className={classes.stepHeader}>hired</Text>;
    }

    return <Text className={classes.stepHeader}>initial</Text>;
};

const RewardMilestoneStartOptionalLabel = (props: {
    billType: BillType;
    startDate: number;
}) => {
    const { startDate, billType } = props;
    const step0Completed = isBefore(startDate, new Date());
    let step0Label = `eligible ${format(startDate, "P")}`;
    if (billType.isSubmission()) {
        step0Label = `presented ${format(startDate, "P")}`;
    } else if (billType.isHire() && !step0Completed) {
        // I don't like this but it's what we have to do since startDate can from from a SQL DATE or DATETIME
        // For hires, we are dealing with a SQL DATE and need to show it regardless of local time
        const originalStartDate = new Date(startDate);
        const adjustedStartDate = new Date(
            originalStartDate.getUTCFullYear(),
            originalStartDate.getUTCMonth(),
            originalStartDate.getUTCDate(),
            originalStartDate.getUTCHours(),
            originalStartDate.getUTCMinutes(),
            originalStartDate.getUTCSeconds()
        );
        step0Label = `starting ${format(adjustedStartDate, "MM/dd/yyyy")}`;
    } else if (billType.isHire() && step0Completed) {
        // I don't like this but it's what we have to do since startDate can from from a SQL DATE or DATETIME
        // For hires, we are dealing with a SQL DATE and need to show it regardless of local time
        const originalStartDate = new Date(startDate);
        const adjustedStartDate = new Date(
            originalStartDate.getUTCFullYear(),
            originalStartDate.getUTCMonth(),
            originalStartDate.getUTCDate(),
            originalStartDate.getUTCHours(),
            originalStartDate.getUTCMinutes(),
            originalStartDate.getUTCSeconds()
        );
        step0Label = `started ${format(adjustedStartDate, "MM/dd/yyyy")}`;
    }
    return <Text variant="body2">{step0Label}</Text>;
};

const RewardMilestoneStatusLabel = (props: {
    rewardMilestone: RewardMilestone;
}) => {
    const {
        accountingStatusTimestamp,
        accountingStatusType,
    } = props.rewardMilestone;

    if (
        accountingStatusType === AccountingStatusType.APPROVED ||
        accountingStatusType === AccountingStatusType.VOID ||
        accountingStatusType === AccountingStatusType.CREDITED
    ) {
        return (
            <Text variant="body2">{`${accountingStatusType.milestone} ${format(
                accountingStatusTimestamp!,
                "P"
            )}`}</Text>
        );
    }

    return <Text variant="body2">{accountingStatusType.milestone}</Text>;
};

const RewardMilestoneOptionalLabel = (props: {
    rewardMilestone: RewardMilestone;
    billType: BillType;
}) => {
    const { billType, rewardMilestone } = props;

    if (billType.isContractHire()) {
        return (
            <Text variant="body2">{`week ending ${rewardMilestone.dueDate}`}</Text>
        );
    }
    return <Text variant="body2">{`eligible ${rewardMilestone.dueDate}`}</Text>;
};
